import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type FieldPath,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type WhereFilterOp,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import Subscription from '../../models/Subscription';

/** Firestore cap for `in` on __name__; callers chunk `documentId()` `in` queries. */
export const SUBSCRIPTION_FIRESTORE_IN_MAX = 30;

type SubscriptionQueryConstraint = {
  field: string | FieldPath;
  op: WhereFilterOp | 'orderBy';
  value: unknown;
};

type GetSubscriptionsByConstraintsOptions = {
  pageSize: number;
  startAfterDocument?: QueryDocumentSnapshot<
    DocumentData,
    DocumentData
  > | null;
};

/**
 * Loads subscriptions with Firestore where / orderBy / limit / optional startAfter.
 */
async function getSubscriptionsByConstraints(
  constraints: SubscriptionQueryConstraint[],
  options: GetSubscriptionsByConstraintsOptions
): Promise<{
  subscriptions: Subscription[];
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData> | null;
  hasMore: boolean;
}> {
  try {
    const coll = collection(firestore, FirestoreCollections.subscriptions);
    const parts: QueryConstraint[] = [];
    let hasOrderBy = false;

    for (const c of constraints) {
      if (c.op === 'orderBy') {
        parts.push(
          orderBy(c.field as string | FieldPath, c.value as 'asc' | 'desc')
        );
        hasOrderBy = true;
      } else {
        parts.push(
          where(c.field as string | FieldPath, c.op as WhereFilterOp, c.value)
        );
      }
    }

    if (!hasOrderBy) {
      throw new GetSubscriptionsByConstraintsFailure(
        'At least one orderBy constraint is required'
      );
    }

    if (options.startAfterDocument) {
      parts.push(startAfter(options.startAfterDocument));
    }

    parts.push(limit(options.pageSize + 1));

    const snap = await getDocs(query(coll, ...parts));
    const docs = snap.docs;
    const hasMore = docs.length > options.pageSize;
    const pageDocs = hasMore ? docs.slice(0, options.pageSize) : docs;
    const subscriptions = pageDocs.map((d) => Subscription.fromFirestore(d));
    const lastVisible =
      pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;

    return { subscriptions, lastVisible, hasMore };
  } catch (e) {
    console.error('getSubscriptionsByConstraints error:', e);
    if (e instanceof GetSubscriptionsByConstraintsFailure) throw e;
    throw new GetSubscriptionsByConstraintsFailure();
  }
}

export class GetSubscriptionsByConstraintsFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getSubscriptionsByConstraints;
